<script lang="ts" setup>
import { ref, watch } from 'vue'
import type { EnqueteFormUserVariableDefinitionFragment } from './EnqueteForm.generated'
import { nanoid } from 'nanoid'
import FlatButton from '@/components/atoms/FlatButton.vue'
import DolphinTextInput from '@/components/atoms/DolphinTextInput.vue'
import EnqueteFormQuestion from './EnqueteFormQuestion.vue'
import EnqueteFormAnsweredChatMessage from './EnqueteFormAnsweredChatMessage.vue'
import type {
  EnqueteFormErrorManager,
  EnqueteFormErrorPath,
  EnqueteFormErrors,
  EnqueteFormType,
} from './formSchema'
import type { EnqueteInput } from '@/types/schema.generated'
import { validEnqueteFormSchema } from './formSchema'
import EnqueteFormError from './EnqueteFormError.vue'
import * as v from 'valibot'
import { useAlert } from '@/libs/alertPortal'

const props = defineProps<{
  initialForm?: EnqueteFormType
  userVariableDefinitions: EnqueteFormUserVariableDefinitionFragment[]
}>()

const emit = defineEmits<{
  (event: 'submit', input: EnqueteInput): void
}>()

const initialForm: EnqueteFormType = props.initialForm ?? {
  title: '',
  questions: [
    {
      key: nanoid(),
      guid: undefined,
      questionText: '',
      userVariableDefinitionGUID: undefined,
      isAnswerRequired: false,
    },
  ],
  answeredChatMessage: undefined,
}

const form = ref<EnqueteFormType>({ ...initialForm })

const formErrors = ref<EnqueteFormErrors>()

const formErrorManager: EnqueteFormErrorManager = {
  get: (path: EnqueteFormErrorPath): string | undefined => {
    const errors = formErrors.value
    if (!errors) {
      return undefined
    }

    const messages = errors.nested?.[path]
    if (!messages) {
      return undefined
    }

    return messages[0]
  },
}

const isSubmittedOnce = ref(false)

watch(form.value, () => {
  if (isSubmittedOnce.value) {
    const result = validate()
    if (!result.success) {
      formErrors.value = result.errors
    }
  }
})

const addQuestion = () => {
  form.value.questions = [
    ...form.value.questions,
    {
      key: nanoid(),
      guid: undefined,
      questionText: '',
      userVariableDefinitionGUID: undefined,
      isAnswerRequired: false,
    },
  ]
}

const moveQuestionToPrevious = (index: number) => {
  if (index === 0) {
    return
  }

  const newIndex = index - 1
  const tmp = form.value.questions[newIndex]

  form.value.questions[newIndex] = form.value.questions[index]
  form.value.questions[index] = tmp
}

const moveQuestionToNext = (index: number) => {
  if (index === form.value.questions.length - 1) {
    return
  }

  const newIndex = index + 1
  const tmp = form.value.questions[newIndex]

  form.value.questions[newIndex] = form.value.questions[index]
  form.value.questions[index] = tmp
}

const deleteQuestion = (index: number) => {
  form.value = {
    ...form.value,
    questions: form.value.questions.filter((_, i) => i !== index),
  }
}

const alert = useAlert()

const handleClickSubmitButton = () => {
  isSubmittedOnce.value = true

  const result = validate()

  if (!result.success) {
    formErrors.value = result.errors

    if (result.errors.nested?.questions) {
      // questions のエラーはフォームに表示しないので、アラートで内容を表示する
      alert.error(result.errors.nested.questions[0])
    } else {
      alert.error('入力内容にエラーがあります。\nご確認ください。')
    }

    return
  }

  emit('submit', result.output)
}

type ValidateResponse =
  | {
      success: true
      output: v.InferOutput<typeof validEnqueteFormSchema>
    }
  | {
      success: false
      errors: EnqueteFormErrors
    }

const validate = (): ValidateResponse => {
  const result = v.safeParse(validEnqueteFormSchema, {
    title: form.value.title,
    questions: form.value.questions,
    answeredChatMessage: form.value.answeredChatMessage,
  })

  formErrors.value = undefined

  if (result.success) {
    return {
      success: true,
      output: result.output,
    }
  } else {
    console.error(result.issues)
    return {
      success: false,
      errors: v.flatten(result.issues),
    }
  }
}
</script>

<template>
  <div :class="$style.form">
    <div :class="$style.formRow">
      <p :class="$style.label">タイトル</p>
      <DolphinTextInput v-model="form.title" />
      <EnqueteFormError v-if="formErrorManager.get('title')">
        {{ formErrorManager.get('title') }}
      </EnqueteFormError>
    </div>
    <div>
      <EnqueteFormQuestion
        v-for="(_, i) in form.questions"
        :key="form.questions[i].key"
        v-model="form.questions[i]"
        :index="i"
        :user-variable-definitions="props.userVariableDefinitions"
        :shown-control="form.questions.length > 1"
        :form-error-manager="formErrorManager"
        @move-next="moveQuestionToNext(i)"
        @move-previous="moveQuestionToPrevious(i)"
        @delete="deleteQuestion(i)"
      />
      <div :class="$style.addQuestionButtonWrapper">
        <FlatButton
          v-if="form.questions.length < 20"
          @click="addQuestion"
          variant="primary"
          >質問を追加する</FlatButton
        >
      </div>
    </div>
    <hr />
    <div>
      <EnqueteFormAnsweredChatMessage
        v-model="form.answeredChatMessage"
        :form-error-manager="formErrorManager"
      />
    </div>
    <div :class="$style.submitButtonWrapper">
      <FlatButton
        @click="handleClickSubmitButton"
        variant="primary"
        size="large"
        >保存する</FlatButton
      >
    </div>
  </div>
</template>

<style lang="scss" module>
.form {
  display: flex;
  flex-direction: column;
  gap: 32px;
}

.formRow {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.label {
  color: $color-text-and-object-high-emphasis;
  font-size: 16px;
  font-weight: bold;
  line-height: 24px;
}

.addQuestionButtonWrapper {
  margin-top: 16px;
}

.submitButtonWrapper {
  display: flex;
  justify-content: center;
}
</style>
